import { useEffect } from "react";

import { Box, Typography } from "@braintrust/braintrust-ui-components";
import { InfoIcon } from "@braintrust/braintrust-ui-components/Icons";
import {
  createCustomJobClicked,
  createJobByUploadClicked,
} from "@js/apps/jobs/actions";
import { Modal } from "@js/components/modal";
import { useAppDispatch, useNavigate } from "@js/hooks";

import { useUploadJobDescriptionModule } from "../../hooks/upload-job-description";

import { CreateJobMenu } from "./create-job-menu";
import {
  openUploadLoadingModal,
  UploadLoadingModalInstance,
} from "./upload-loading-modal";

import styles from "./style.module.scss";

export const CreateJobModalInstance = Modal("create-job-modal", {
  keepOnBackdropClick: false,
  className: styles.createJobModal,
  closeButton: true,
  closeButtonProps: {
    variant: "tertiary",
    size: "x-small",
  },
});

const CreateJobModal = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    getInputProps,
    onFileUpload,
    uploadingFileName,
    uploadError,
    isUploading,
    updateJobFormWithFileDataError,
    updateJobFormWithFileDataFinished,
  } = useUploadJobDescriptionModule();

  useEffect(() => {
    if (isUploading) {
      openUploadLoadingModal({ uploadingFileName });
    } else {
      UploadLoadingModalInstance.close();
    }
  }, [isUploading, uploadingFileName]);

  useEffect(() => {
    if (updateJobFormWithFileDataFinished) {
      navigate("/jobs/add_new");
    }
  }, [navigate, updateJobFormWithFileDataFinished]);

  useEffect(() => {
    return () => CreateJobModalInstance.close();
  }, []);

  const createJobOptions = [
    {
      id: "custom-job" as const,
      disabled: false,
      icon: `${SETTINGS.STATIC_URL}jobs/custom-job-icon.svg`,
      onClick: () => {
        dispatch(createCustomJobClicked());
        navigate("/jobs/add_new");
      },
      title: "Create a custom job",
    },
    {
      id: "upload-job" as const,
      disabled: false,
      icon: `${SETTINGS.STATIC_URL}jobs/upload-job-icon.svg`,
      onClick: () => {
        dispatch(createJobByUploadClicked());
        onFileUpload();
      },
      subText: "Max 10 MB",
      title: "Upload existing job description",
    },
  ];

  return (
    <>
      {!isUploading && (
        <Box>
          <Typography component="h2" fontWeight={500} size="medium">
            Create a job
          </Typography>
          <Typography
            component="h1"
            fontWeight={400}
            style={{ fontSize: 26 }}
            sx={{ mt: 2 }}
          >
            How would you like to set up your next job?
          </Typography>
          {(uploadError || updateJobFormWithFileDataError) && (
            <Box
              sx={{
                background: "var(--soft-red)",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: 1,
                padding: 1,
                marginBottom: 2,
                width: "fit-content",
                marginTop: 4,
              }}
            >
              <InfoIcon sx={{ color: "var(--negative-2)" }} />
              <Typography
                component="p"
                color="negative"
                size="small"
                fontWeight={500}
              >
                {uploadError || updateJobFormWithFileDataError}
              </Typography>
            </Box>
          )}
          <CreateJobMenu createJobOptions={createJobOptions} />
          <input
            data-testid="upload-job-description-input"
            {...getInputProps()}
            style={{ display: "none" }}
          />
        </Box>
      )}
      <UploadLoadingModalInstance />
    </>
  );
};

export const openCreateJobModal = () =>
  CreateJobModalInstance.open({
    onClose: () => CreateJobModalInstance.close(),
    children: <CreateJobModal />,
  });
